/**
 * @flow weak
 */
import { eventChannel } from 'redux-saga';
import { call, put, all, select, take, takeEvery } from 'redux-saga/effects';

// Helpers
import { loadLocaleData, localeMap } from '../../lib/locale-loader';
import negotiateLocale from '../../lib/negotiate-locale';
import { updateProfile } from '../../lib/api-helper';
import { setItem } from '../../lib/local-storage';

// Getters
import { getUsersLocale } from '../session/getters';
import { locale as getCurrentLocale } from './getters';

// Constants
import { SESSION_FETCH_SUCCESS } from '../session/actions';
export const CHANGE_LOCALE_PENDING = 'CHANGE_LOCALE_PENDING';
export const CHANGE_LOCALE_SUCCESS = 'CHANGE_LOCALE_SUCCESS';
export const CHANGE_LOCALE_ERROR = 'CHANGE_LOCALE_ERROR';
export const LOCAL_STORAGE_LOCALE = 'locale';

export function loadLocale(locale) {
  return loadLocaleData(locale).then(([locale, messages]) => {
    return { locale, messages };
  });
}

// Resolve a value that arrived from outside this app to a locale we actually support,
// or to nothing. Anything unresolvable must be dropped rather than passed on:
// loadLocaleData silently coerces an unrecognised locale to DEFAULT_LOCALE, and
// changeLocale would then save that English fallback to the user's account as though
// they had chosen it (UNI-2795). The value is not guaranteed to be a string —
// storageEvent.newValue is null when the key is removed, and other apps on this origin
// can write anything to it.
// Lowercased before negotiating because negotiateLocale echoes back the casing it was
// handed, while localeMap's keys and loadLocaleData's switch are both lowercase — 'DE'
// would otherwise pass this check and then load English anyway.
export const resolveSupportedLocale = (value: mixed): ?string => {
  if (typeof value !== 'string') return undefined;

  return negotiateLocale([value.toLowerCase()], Object.keys(localeMap));
};

export const switchLocale = (locale) => {
  return {
    type: CHANGE_LOCALE_PENDING,
    locale
  };
};

// On every 'CHANGE_LOCALE_PENDING' action (from the locale DDL in the footer)
// or 'SESSION_FETCH_SUCCESS' action (on app init (current_user/exchange))
// run the `changeLocale` function below.
export function* changeLocalePending() {
  yield all([
    takeEvery(CHANGE_LOCALE_PENDING, changeLocale),
    takeEvery(SESSION_FETCH_SUCCESS, changeLocale)
  ]);
}

export function* applyLocale(locale) {
  const data = yield loadLocale(locale);

  yield put({ type: CHANGE_LOCALE_SUCCESS, messages: data.messages, locale: data.locale });
  // save our new value to localStorage
  setItem(LOCAL_STORAGE_LOCALE, data.locale);

  return data.locale;
}

export function* changeLocale({ locale })  {
  try {
    const sessionLocale = yield select(getUsersLocale);

    let localeToUse = locale;

    if (sessionLocale && !locale) {
      localeToUse = sessionLocale; // Set locale from user session
    }

    const appliedLocale = yield call(applyLocale, localeToUse);

    if (sessionLocale && locale) {
      yield updateProfile(appliedLocale); // If user session and user changed locale - save to profile
    }
  }
  catch (error) {
    yield put({ type: CHANGE_LOCALE_ERROR, error });
  }
}

// Never saves to the account: another tab's write is not the user choosing a language,
// and the account language drives invoice, report and email language (UNI-2795).
export function* changeLocaleFromOtherTab(locale) {
  try {
    yield call(applyLocale, locale);
  }
  catch (error) {
    yield put({ type: CHANGE_LOCALE_ERROR, error });
  }
}

// This is a little weird - setupLocalStorageLocalChange
// is called from our rootSaga, so this gets called on app init.
// From here, we create a 'channel' which will listen to all
// callback events from window.addEventListener('storage', ...);
// Inside the callback, we can use a special function (emitter)
// to send those callback events back into our redux-saga
// land where we use generators to fire actions/events.
// We have to do this since, a callback to window.addEventListener
// expects an old school function, or an arrow funciton, which cannot
// interact with generators in anyway.
export function* setupLocalStorageLocaleChange() {
  yield localStorageChannel();
}

// respond to locale updates from other tabs
export function* localStorageChannel() {
  const channel = eventChannel(emitter => {
    window.addEventListener('storage', function(storageEvent) {
      if (storageEvent.key === LOCAL_STORAGE_LOCALE) {
        emitter(storageEvent.newValue);
      }
    });
    return () => null;
  });

  while (true) {
    const newLocaleValue = yield take(channel);

    // Drop anything we cannot name. Without this, a bad write from any app sharing this
    // origin becomes an English fallback that we save to the user's account.
    const incomingLocale = resolveSupportedLocale(newLocaleValue);
    if (!incomingLocale) continue;

    // Drop no-op echoes. Apps on this origin rewrite this key on every page load, so
    // most events carry the locale we are already rendering.
    const currentLocale = yield select(getCurrentLocale);
    if (incomingLocale === currentLocale) continue;

    yield changeLocaleFromOtherTab(incomingLocale);
  }
}



// WEBPACK FOOTER //
// ./src/modules/locale/actions.js